import { useEffect, useState } from "react";
import { OrderDispatch } from "src/types";
import * as mvteApi from "src/api/mvte.api";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import dayjs from "dayjs";

export function OrderDispatches({
    orderNumber,
}: {
    orderNumber: string | null;
}) {
    //TODO: Replace this with react-query implementation
    //TODO Replacing this with react-query will require us to update our test :(
    const [orderDispatches, setOrderDispatches] = useState<OrderDispatch[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentDispatchIdx, setCurrentDispatchIdx] = useState(0);

    useEffect(() => {
        if (orderNumber === null) return;

        // Reset dispatch index when the order changes.
        setCurrentDispatchIdx(0);

        setIsLoading(true);

        mvteApi.fetchOrderDispatches({ orderNumber }).then((data) => {
            const sortedAscByDispatch = data?.sort(
                (a, b) => Number(a.dispatchNum) - Number(b.dispatchNum),
            );

            setOrderDispatches(sortedAscByDispatch);
            setIsLoading(false);
        });
    }, [orderNumber]);

    if (!orderNumber) {
        return (
            <div className="bg-white rounded-sm p-3 h-full flex items-center justify-center text-gray-500">
                Select order number to view dispatch details
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="bg-white rounded-sm p-3 h-full flex items-center justify-center text-gray-500">
                <span>Loading dispatch information...</span>
            </div>
        );
    }

    if (orderDispatches.length === 0) {
        return (
            <div className="bg-white rounded-sm p-3 h-full flex items-center justify-center text-gray-500">
                No dispatches found for order #{orderNumber}
            </div>
        );
    }

    return (
        <div className="bg-white rounded-sm overflow-y-auto h-full">
            <div className="px-3 py-3 flex justify-between items-center sticky top-0 bg-white">
                <span className="text-gray-500 font-medium">
                    Dispatches ({orderDispatches.length})
                </span>
                <span className="space-x-2 flex">
                    <button
                        className="text-gray-500 disabled:text-gray-500/50"
                        disabled={currentDispatchIdx === 0}
                        onClick={() => {
                            setCurrentDispatchIdx((prev) => prev - 1);
                        }}
                    >
                        <FaChevronLeft />
                    </button>
                    <span className="text-gray-500 font-medium">
                        {currentDispatchIdx + 1} / {orderDispatches?.length}
                    </span>
                    <button
                        className="text-gray-500 disabled:text-gray-500/50"
                        disabled={
                            currentDispatchIdx === orderDispatches.length - 1
                        }
                        onClick={() => {
                            setCurrentDispatchIdx((prev) => prev + 1);
                        }}
                    >
                        <FaChevronRight />
                    </button>
                </span>
            </div>
            <div className="bg-white rounded-sm px-3 pb-3 max-w-3xl text-sm">
                <div className="grid grid-cols-4 gap-2 mb-2 pb-2 border-b">
                    <div>
                        <div className="text-gray-500 text-xs">Order #</div>
                        <div className="font-medium">
                            {orderDispatches[currentDispatchIdx].orderNumber}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">Status</div>
                        <div className="font-medium">
                            {orderDispatches[currentDispatchIdx].orderStatus ??
                                "N/A"}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">Dispatch #</div>
                        <div className="font-medium">
                            {orderDispatches[currentDispatchIdx].dispatchNum ??
                                "N/A"}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">
                            End DateTime
                        </div>
                        <div className="font-medium">
                            {orderDispatches[currentDispatchIdx]
                                .dispatchEndDateTime
                                ? dayjs(
                                      orderDispatches[currentDispatchIdx]
                                          .dispatchEndDateTime,
                                  ).format("D MMM YYYY h:mm A")
                                : "N/A"}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-4 gap-2">
                    <div>
                        <div className="text-gray-500 text-xs">Unit</div>
                        <div>
                            {orderDispatches[currentDispatchIdx].tractorCode ??
                                "N/A"}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">Driver 1</div>
                        <div>
                            {orderDispatches[currentDispatchIdx].driver1Code ??
                                "N/A"}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">Driver 2</div>
                        <div>
                            {orderDispatches[currentDispatchIdx].driver2Code ??
                                "N/A"}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">CSR</div>
                        <div>
                            {orderDispatches[currentDispatchIdx].csrCode ??
                                "N/A"}
                        </div>
                    </div>

                    <div>
                        <div className="text-gray-500 text-xs">Origin</div>
                        <div>
                            {orderDispatches[currentDispatchIdx].originName ??
                                "N/A"}
                        </div>
                    </div>

                    <div>
                        <div className="text-gray-500 text-xs">Load At</div>
                        <div>
                            {orderDispatches[currentDispatchIdx]
                                .loadAtCustomerCode ?? "N/A"}
                        </div>
                    </div>

                    <div>
                        <div className="text-gray-500 text-xs">Destination</div>
                        <div>
                            {orderDispatches[currentDispatchIdx]
                                .destinationName ?? "N/A"}
                        </div>
                    </div>

                    <div>
                        <div className="text-gray-500 text-xs">Bill To</div>
                        <div>
                            {orderDispatches[currentDispatchIdx]
                                .billToCustomerCode ?? "N/A"}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">Consignee</div>
                        <div>
                            {orderDispatches[currentDispatchIdx]
                                .consigneeCustomerCode ?? "N/A"}
                        </div>
                    </div>

                    <div>
                        <div className="text-gray-500 text-xs">Fleet Code</div>
                        <div>
                            {orderDispatches[currentDispatchIdx].fleetCode ??
                                "N/A"}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">FM Name</div>
                        <div>
                            {orderDispatches[currentDispatchIdx]
                                .fleetManagerName ?? "N/A"}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
